import {inject, Injectable} from "@angular/core";
import {Database, objectVal, ref} from "@angular/fire/database";
import {combineLatest, map, Observable, startWith, tap} from "rxjs";
import {Region, Regions, RegionStatus, ReleaseStatus, Team} from "./models";

const CACHE_KEY_PREFIX = 'divisions-cache-';

export const regionList = [
  {
    key: 'world',
    name: 'World',
  },
  {
    key: 'michigan',
    name: 'Michigan',
  },
  {
    key: 'ne',
    name: 'New England',
  },
  {
    key: 'ontario',
    name: 'Ontario',
  },
  {
    key: 'texas',
    name: 'Texas',
  },
];

type SummaryTeam = Team&{percentile: number};

interface DivisionSummary {
  key: string;
  name: string;
  topTeams: SummaryTeam[];
  teamCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class DivisionsService {
  private readonly db = inject(Database);
  private readonly cache: Regions = {};

  private getRegionWithoutCache(regionKey: string): Observable<Region|null> {
    return objectVal<Region>(ref(this.db, `regions/${regionKey}`)).pipe(
      tap(region => this.updateCache(regionKey, region)),
    );
  }

  getRegion(regionKey: string): Observable<Region|null> {
    return this.getRegionWithoutCache(regionKey).pipe(
      startWith(this.getCache(regionKey)),
    );
  }

  getTeamList(regionKey: string, sortBy: 'teamNumber'|'epa' = 'teamNumber', divisionKey?: string): Observable<Team[]> {
    return this.getRegion(regionKey).pipe(map(region => {
      const teamList: Team[] = [];

      for(const t of Object.values(region?.teams || {})) {
        if(t && (!divisionKey || t.divisionKey === divisionKey)) {
          teamList.push(t);
        }
      }

      teamList.sort((a, b) => {
        if(sortBy === 'epa') {
          return (b.stats?.epa || 0) - (a.stats?.epa || 0);
        }
        return a[sortBy]?.localeCompare(b[sortBy], undefined, {numeric: true});
      });

      return teamList;
    }));
  }

  getDivisionSummaries(regionKey: string) {
    return combineLatest([this.getRegion(regionKey), this.getTeamList(regionKey, 'epa')]).pipe(map(([region, teams]) => {
      const summaries: DivisionSummary[] = [];
      const topTeamCount = Object.keys(region?.divisions || {}).length * 3;
      const topTeams = teams.slice(0, topTeamCount);
      const min = topTeams[topTeams.length - 1]?.stats?.epa || 0;
      const range = ((topTeams[0]?.stats?.epa || 0) - min) || 1;

      for(const [key, d] of Object.entries(region?.divisions || {})) {
        summaries.push({
          key,
          name: d.name,
          topTeams: topTeams.filter(t => t.divisionKey === key).map(t => {
            const s = t as SummaryTeam;
            s.percentile = ((s.stats?.epa || 0) - min) / range;
            return s;
          }),
          teamCount: teams.filter(t => t.divisionKey === key).length,
        });
      }

      summaries.sort((a, b) => a.name.localeCompare(b.name));

      return summaries;
    }));
  }

  getStatus(regionKey: string): Observable<RegionStatus> {
    return this.getLiveRegion(regionKey).pipe(map(region => {
      let count = 0;
      let countWithDivisions = 0;
      for(const t of Object.values(region?.teams || {})) {
        count++;
        if(t?.divisionKey) {
          countWithDivisions++;
        }
      }

      let status: ReleaseStatus = 'none';
      if(countWithDivisions) {
        if(countWithDivisions === count) {
          status = 'all'
        } else {
          status = 'some';
        }
      }

      return {status};
    }));
  }

  private getLiveRegion(regionKey: string): Observable<Region|null> {
    const cache = this.getCache(regionKey);
    if(Object.values(cache?.teams || {}).find(t => !!t?.divisionKey)) {
      // Cache has divisions, just return them.
      return this.getRegion(regionKey);
    }

    return this.getRegionWithoutCache(regionKey);
  }

  private getCache(regionKey: string): Region|null {
    if(this.cache[regionKey]) {
      return this.cache[regionKey];
    }

    try {
      const c = window.localStorage.getItem(CACHE_KEY_PREFIX + regionKey);
      if(c) {
        this.cache[regionKey] = JSON.parse(c);
        return this.cache[regionKey];
      }
    } catch (e) {
      console.log('Unable to load cache for', regionKey);
    }

    return null;
  }

  private updateCache(regionKey: string, region: Region) {
    if(!region) return;

    this.cache[regionKey] = region;

    try {
      window.localStorage.setItem(CACHE_KEY_PREFIX + regionKey, JSON.stringify(region));
    } catch (e) {
      console.log('Unable to save cache for', regionKey);
    }
  }
}
